import React, { useState, useEffect, useRef } from 'react';
import {
    AiOutlinePlus,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineDownload,
    AiOutlinePrinter,
    AiOutlinePlayCircle,
    AiOutlineClockCircle,
    AiOutlineEye,
    AiOutlineVideoCamera,
    AiOutlineCalendar,
    AiOutlineUser,
    AiOutlinePauseCircle,
    AiOutlineStop,
    AiOutlineFullscreen,
    AiOutlineAppstore,
    AiOutlineUnorderedList,
} from 'react-icons/ai';
import { Whammy } from '../../whammy/whammy.js';
import myAxios from '../AxiosUrl';
import Draw from './Draw.js';
import ReactPlayer from 'react-player';
import styles from './VisitorVideo.module.css';

function VisitorVideo() {
    const [videoBlob, setVideoBlob] = useState();
    const [videoList, setVideoList] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [viewMode, setViewMode] = useState('list');
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const uid = localStorage.getItem('id');
    const canvasRef = useRef(null);
    const playerRef = useRef(null);
    const animationRef = useRef(null);

    useEffect(() => {
        fetchVideoList();
    }, []);

    const fetchVideoList = () => {
        myAxios
            .get(`/get-video-list/${uid}`)
            .then((res) => {
                setVideoList(res.data);
            })
            .catch((err) => {
                console.log(err.response?.data || err.message);
            });
    };

    const handleCreateVideo = (VideoData) => {
        const videoArray = VideoData.data.map((data) => data.imageUrl);
        console.log(videoArray);
        setVideoBlob(videoArray);
        setSelectedVideo(VideoData);
        setCurrentFrameIndex(0);
        setDuration(videoArray.length);
        setCurrentTime(0);
        setIsPlaying(false);
    };

    // Animation loop for canvas playback
    useEffect(() => {
        if (!selectedVideo || !videoBlob || !isPlaying) {
            if (animationRef.current) {
                clearTimeout(animationRef.current);
            }
            return;
        }

        const playFrame = () => {
            if (!canvasRef.current || !videoBlob || currentFrameIndex >= videoBlob.length) {
                setIsPlaying(false);
                return;
            }

            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = videoBlob[currentFrameIndex];

            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                setCurrentTime(currentFrameIndex);
                setCurrentFrameIndex((prev) => prev + 1);

                if (currentFrameIndex < videoBlob.length - 1) {
                    animationRef.current = setTimeout(playFrame, 100);
                } else {
                    setIsPlaying(false);
                    setCurrentFrameIndex(0);
                }
            };

            img.onerror = () => {
                console.error('Failed to load image:', videoBlob[currentFrameIndex]);
                setCurrentFrameIndex((prev) => prev + 1);
            };
        };

        playFrame();

        return () => {
            if (animationRef.current) {
                clearTimeout(animationRef.current);
            }
        };
    }, [isPlaying, currentFrameIndex, selectedVideo, videoBlob]);

    // Initialize canvas when modal opens
    useEffect(() => {
        if (selectedVideo && canvasRef.current && videoBlob && videoBlob.length > 0) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            canvas.width = 800;
            canvas.height = 600;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = videoBlob[0];
            img.onload = () => {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            };
        }
    }, [selectedVideo, videoBlob]);

    const formatCurrency = (amount) => {
        return `৳${amount.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const filteredVideos = videoList.filter((video) => {
        const matchesSearch =
            video._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (video.data &&
                video.data.some(
                    (item) =>
                        item.imageUrl &&
                        item.imageUrl
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                ));
        return matchesSearch;
    });

    const handlePlayPause = () => {
        if (isPlaying) {
            setIsPlaying(false);
            if (animationRef.current) {
                clearTimeout(animationRef.current);
            }
        } else {
            setIsPlaying(true);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newFrameIndex = Math.floor((clickX / rect.width) * duration);
        setCurrentFrameIndex(newFrameIndex);
        setCurrentTime(newFrameIndex);
        setIsPlaying(false);
    };

    const handleCloseModal = () => {
        setSelectedVideo(null);
        setIsPlaying(false);
        setCurrentFrameIndex(0);
        setCurrentTime(0);
        if (animationRef.current) {
            clearTimeout(animationRef.current);
        }
    };

    return (
        <div className={styles.videoContainer}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>
                        Visitor Video Analytics
                    </h1>
                    <p className={styles.pageSubtitle}>
                        Track and analyze visitor behavior through recorded
                        sessions
                    </p>
                </div>
                <div className={styles.headerRight}>
                    <button className={styles.primaryButton}>
                        <AiOutlinePlus className={styles.buttonIcon} />
                        Record New Session
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineVideoCamera />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {videoList.length}
                        </div>
                        <div className={styles.statLabel}>Total Sessions</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineClockCircle />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {videoList.reduce(
                                (total, video) =>
                                    total +
                                    (video.data ? video.data.length : 0),
                                0
                            )}
                        </div>
                        <div className={styles.statLabel}>Total Frames</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineUser />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {
                                new Set(
                                    videoList.map(
                                        (video) => video.userId || 'anonymous'
                                    )
                                ).size
                            }
                        </div>
                        <div className={styles.statLabel}>Unique Visitors</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineCalendar />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {
                                videoList.filter((video) => {
                                    const videoDate = new Date(
                                        video.createdAt || Date.now()
                                    );
                                    const today = new Date();
                                    return (
                                        videoDate.toDateString() ===
                                        today.toDateString()
                                    );
                                }).length
                            }
                        </div>
                        <div className={styles.statLabel}>Today's Sessions</div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className={styles.filtersSection}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search sessions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                    <AiOutlineSearch className={styles.searchIcon} />
                </div>
                <div className={styles.filterButtons}>
                    <div className={styles.viewToggle}>
                        <button
                            className={`${styles.viewButton} ${
                                viewMode === 'grid' ? styles.activeView : ''
                            }`}
                            onClick={() => setViewMode('grid')}
                            title="Grid View"
                        >
                            <AiOutlineAppstore />
                        </button>
                        <button
                            className={`${styles.viewButton} ${
                                viewMode === 'list' ? styles.activeView : ''
                            }`}
                            onClick={() => setViewMode('list')}
                            title="List View"
                        >
                            <AiOutlineUnorderedList />
                        </button>
                    </div>
                    <button className={styles.actionButton}>
                        <AiOutlineDownload className={styles.actionIcon} />
                        Export
                    </button>
                    <button className={styles.actionButton}>
                        <AiOutlinePrinter className={styles.actionIcon} />
                        Print
                    </button>
                </div>
            </div>

            {/* Video List Grid/List */}
            <div
                className={
                    viewMode === 'grid'
                        ? styles.videoGrid
                        : styles.videoList
                }
            >
                {filteredVideos.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>
                            <AiOutlineVideoCamera />
                        </div>
                        <h3 className={styles.emptyTitle}>
                            No video sessions found
                        </h3>
                        <p className={styles.emptyDescription}>
                            {searchTerm
                                ? 'Try adjusting your search criteria'
                                : 'Start recording visitor sessions to see them here'}
                        </p>
                        {!searchTerm && (
                            <button className={styles.emptyActionButton}>
                                Start Recording
                            </button>
                        )}
                    </div>
                ) : (
                    filteredVideos.map((video, index) => (
                        <div
                            key={video._id || index}
                            className={
                                viewMode === 'grid'
                                    ? styles.videoCard
                                    : styles.videoListItem
                            }
                        >
                            {viewMode === 'grid' ? (
                                <>
                                    <div className={styles.videoThumbnail}>
                                        <div className={styles.videoPreview}>
                                            <canvas
                                                ref={index === 0 ? canvasRef : null}
                                                className={styles.videoCanvas}
                                            />
                                            <div className={styles.videoOverlay}>
                                                <button
                                                    className={styles.playButton}
                                                    onClick={() =>
                                                        handleCreateVideo(video)
                                                    }
                                                >
                                                    <AiOutlinePlayCircle />
                                                </button>
                                            </div>
                                        </div>
                                        <div className={styles.videoDuration}>
                                            {formatDuration(
                                                video.data ? video.data.length : 0
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.videoInfo}>
                                        <div className={styles.videoTitle}>
                                            Session {video._id.slice(-8)}
                                        </div>
                                        <div className={styles.videoMeta}>
                                            <div className={styles.metaItem}>
                                                <AiOutlineCalendar
                                                    className={styles.metaIcon}
                                                />
                                                {formatDate(
                                                    video.createdAt || new Date()
                                                )}
                                            </div>
                                            <div className={styles.metaItem}>
                                                <AiOutlineClockCircle
                                                    className={styles.metaIcon}
                                                />
                                                {video.data ? video.data.length : 0}{' '}
                                                frames
                                            </div>
                                        </div>
                                        <div className={styles.videoActions}>
                                            <button
                                                className={styles.actionBtn}
                                                onClick={() => handleCreateVideo(video)}
                                                title="Play Video"
                                            >
                                                <AiOutlinePlayCircle />
                                            </button>
                                            <button
                                                className={styles.actionBtn}
                                                title="View Details"
                                            >
                                                <AiOutlineEye />
                                            </button>
                                            <button
                                                className={styles.actionBtn}
                                                title="Download"
                                            >
                                                <AiOutlineDownload />
                                            </button>
                                            <button
                                                className={styles.actionBtn}
                                                title="Delete"
                                            >
                                                <AiOutlineDelete />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // List View
                                <>
                                    <div className={styles.listItemThumbnail}>
                                        <canvas className={styles.listCanvas} />
                                        <div className={styles.listOverlay}>
                                            <AiOutlinePlayCircle />
                                        </div>
                                    </div>
                                    <div className={styles.listItemContent}>
                                        <div className={styles.listItemHeader}>
                                            <h3 className={styles.listItemTitle}>
                                                Session {video._id.slice(-8)}
                                            </h3>
                                            <span className={styles.listItemBadge}>
                                                {video.data ? video.data.length : 0} frames
                                            </span>
                                        </div>
                                        <div className={styles.listItemMeta}>
                                            <div className={styles.listMetaItem}>
                                                <AiOutlineCalendar />
                                                <span>
                                                    {formatDate(
                                                        video.createdAt || new Date()
                                                    )}
                                                </span>
                                            </div>
                                            <div className={styles.listMetaItem}>
                                                <AiOutlineClockCircle />
                                                <span>
                                                    {formatDuration(
                                                        video.data ? video.data.length : 0
                                                    )}
                                                </span>
                                            </div>
                                            <div className={styles.listMetaItem}>
                                                <AiOutlineUser />
                                                <span>{video.userId || 'Anonymous'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.listItemActions}>
                                        <button
                                            className={styles.listActionBtn}
                                            onClick={() => handleCreateVideo(video)}
                                        >
                                            <AiOutlinePlayCircle />
                                            <span>Play</span>
                                        </button>
                                        <button className={styles.listActionBtn}>
                                            <AiOutlineEye />
                                            <span>View</span>
                                        </button>
                                        <button className={styles.listActionBtn}>
                                            <AiOutlineDownload />
                                            <span>Download</span>
                                        </button>
                                        <button className={`${styles.listActionBtn} ${styles.deleteBtn}`}>
                                            <AiOutlineDelete />
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Video Player Modal */}
            {selectedVideo && (
                <div className={styles.playerModal} onClick={handleCloseModal}>
                    <div
                        className={styles.playerContainer}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.playerHeader}>
                            <h3 className={styles.playerTitle}>
                                Session {selectedVideo._id.slice(-8)}
                            </h3>
                            <button
                                className={styles.closeButton}
                                onClick={handleCloseModal}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.playerContent}>
                            <div className={styles.videoPlayer}>
                                <canvas
                                    ref={canvasRef}
                                    className={styles.mainCanvas}
                                    width={800}
                                    height={600}
                                />
                                <div className={styles.playerControls}>
                                    <button
                                        className={styles.controlButton}
                                        onClick={handlePlayPause}
                                    >
                                        {isPlaying ? (
                                            <AiOutlinePauseCircle />
                                        ) : (
                                            <AiOutlinePlayCircle />
                                        )}
                                    </button>
                                    <div
                                        className={styles.progressBar}
                                        onClick={handleSeek}
                                    >
                                        <div
                                            className={styles.progressFill}
                                            style={{
                                                width: `${
                                                    (currentTime / duration) *
                                                    100
                                                }%`,
                                            }}
                                        />
                                    </div>
                                    <div className={styles.timeDisplay}>
                                        {formatDuration(currentTime)} /{' '}
                                        {formatDuration(duration)}
                                    </div>
                                    <button className={styles.controlButton}>
                                        <AiOutlineFullscreen />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default VisitorVideo;
